import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from "recharts";


// ---------------------------------------------------------------------
// CHART SECTION
//
// Renders one horizontal incident-type bar chart for one statistical
// period and one incident family.
//
// Examples:
//   January - Public Incident Types
//   January - Responder Incident Types
//   Q1 - Public Incident Types
//   2026 - Public Incident Types
//
// Zero-count incident types are removed before data reaches this
// component so the chart remains readable.
// ---------------------------------------------------------------------
const ChartSection = ({
    title,
    subtitle,
    data,
    color,
    pdfId,
}) => {
    if (!Array.isArray(data) || data.length === 0) {
        return (
            <div
                data-pdf-chart={pdfId}
                style={{
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "20px",
                    marginBottom: "24px",
                }}
            >
                <h4 style={{ marginBottom: "4px" }}>
                    {title}
                </h4>

                {subtitle && (
                    <div
                        style={{
                            color: "#666",
                            marginBottom: "12px",
                        }}
                    >
                        {subtitle}
                    </div>
                )}

                <div
                    style={{
                        color: "#777",
                        fontStyle: "italic",
                    }}
                >
                    No incidents reported for this period.
                </div>
            </div>
        );
    }

    // Give larger datasets enough vertical room so labels and bars
    // remain readable instead of being compressed together.
    const chartHeight = Math.max(
        260,
        data.length * 38
    );

    return (
        <div
            data-pdf-chart={pdfId}
            style={{
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "20px",
                marginBottom: "24px",
            }}
        >
            <h4 style={{ marginBottom: "4px" }}>
                {title}
            </h4>

            {subtitle && (
                <div
                    style={{
                        color: "#666",
                        marginBottom: "18px",
                    }}
                >
                    {subtitle}
                </div>
            )}

            <div
                style={{
                    width: "100%",
                    height: chartHeight,
                }}
            >
                <ResponsiveContainer
                    width="100%"
                    height="100%"
                >
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{
                            top: 5,
                            right: 65,
                            left: 40,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                        />

                        <XAxis
                            type="number"
                            allowDecimals={false}
                        />

                        <YAxis
                            type="category"
                            dataKey="name"
                            width={260}
                            interval={0}
                            tick={{ fontSize: 13 }}
                        />

                        <Tooltip />

                        <Bar
                            dataKey="count"
                            name="Incidents"
                            fill={color}
                        >
                            <LabelList
                                dataKey="count"
                                position="right"
                                style={{
                                    fill: "#333",
                                    fontWeight: 600,
                                }}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};


// ---------------------------------------------------------------------
// STATISTICAL MATRIX CHARTS
//
// Uses the same statistical matrix as View Spreadsheet.
//
// Public and Responder incident types remain independent.
//
// The backend columns determine the chart grouping. This means the
// component automatically supports:
//
//   Days in Month
//   Months in Year
//   Quarters in Year
//   Year
//
// without duplicating period-specific frontend logic.
// ---------------------------------------------------------------------
const StatsCharts = ({ chartData }) => {
    if (!chartData) {
        return null;
    }

    const columns = Array.isArray(
        chartData?.columns
    )
        ? chartData.columns
        : [];

    const rows = Array.isArray(
        chartData?.rows
    )
        ? chartData.rows
        : [];

    const publicRows = rows.filter(
        (row) =>
            row?.incident_family === "public"
    );

    const responderRows = rows.filter(
        (row) =>
            row?.incident_family === "responder"
    );


    // -------------------------------------------------------------
    // CONVERT ONE MATRIX COLUMN INTO BAR-CHART DATA
    //
    // Only positive counts are included. Configured incident types
    // with zero incidents remain part of the statistical matrix but
    // do not clutter the visual chart.
    // -------------------------------------------------------------
    const buildChartData = (
        familyRows,
        columnKey
    ) =>
        familyRows
            .map((row) => ({
                name:
                    row?.incident_type ||
                    "Untitled Incident Type",

                count:
                    Number(
                        row?.values?.[
                            columnKey
                        ]
                    ) || 0,
            }))
            .filter(
                (item) =>
                    item.count > 0
            );


    // -------------------------------------------------------------
    // FAMILY TOTAL
    //
    // Used only for the informational heading. Public and Responder
    // totals are intentionally calculated independently.
    // -------------------------------------------------------------
    const getFamilyTotal = (familyRows) =>
        familyRows.reduce(
            (sum, row) =>
                sum +
                (Number(row?.total) || 0),
            0
        );

    const publicTotal =
        getFamilyTotal(publicRows);

    const responderTotal =
        getFamilyTotal(responderRows);


    // -------------------------------------------------------------
    // RENDER ONE FAMILY
    // -------------------------------------------------------------
    const renderFamilyCharts = (
        familyName,
        familyRows,
        familyTotal,
        color
    ) => {
        if (familyRows.length === 0) {
            return null;
        }

        const familyKey =
            familyName.toLowerCase();

        return (
            <div
                style={{
                    marginBottom: "38px",
                }}
            >
                <div
                    style={{
                        marginBottom: "18px",
                        paddingBottom: "10px",
                        borderBottom:
                            "2px solid #ddd",
                    }}
                >
                    <h3
                        style={{
                            marginBottom: "4px",
                        }}
                    >
                        {familyName} Incident Statistics
                    </h3>

                    <div
                        style={{
                            color: "#666",
                        }}
                    >
                        Total Incidents:{" "}
                        <strong>
                            {familyTotal}
                        </strong>
                    </div>
                </div>

                {columns.map((column) => {
                    const data =
                        buildChartData(
                            familyRows,
                            column.key
                        );

                    return (
                        <ChartSection
                            key={
                                `${familyKey}-` +
                                column.key
                            }
                            pdfId={
                                `${familyKey}-` +
                                column.key
                            }
                            title={
                                `${column.label} — ` +
                                `${familyName} Incident Types`
                            }
                            subtitle={
                                "Incident counts for this reporting period."
                            }
                            data={data}
                            color={color}
                        />
                    );
                })}
            </div>
        );
    };


    return (
        <div style={{ marginTop: "30px" }}>
            {renderFamilyCharts(
                "Public",
                publicRows,
                publicTotal,
                "#3872ad"
            )}

            {renderFamilyCharts(
                "Responder",
                responderRows,
                responderTotal,
                "#0c387f"
            )}
        </div>
    );
};

export default StatsCharts;